/**
 * Entitlement utilities for test user tier overrides.
 *
 * Test users (profiles.is_test_user = true) can toggle between
 * Free/Social/Pro tiers to preview gated experiences without real billing.
 * test_pro_mode is bounded to 'auto'|'free'|'social'|'pro' by DB CHECK constraint.
 */

export type EntitlementTier = 'free' | 'social' | 'pro';
export type TestProMode = 'auto' | 'free' | 'social' | 'pro';

export interface EntitlementProfile {
  is_pro?: boolean;
  is_test_user?: boolean;
  test_pro_mode?: TestProMode;
  subscription_tier?: string;
  billing_source?: string;
  entitlement_expires_at?: string | null;
}

/**
 * Get the effective tier for a user.
 *
 * Priority:
 * 1. Test user override (when is_test_user && test_pro_mode !== 'auto')
 * 2. Manual entitlement expiry check (if manual + expired → free)
 * 3. subscription_tier (canonical, when present and not 'free')
 * 4. Legacy is_pro boolean fallback
 *
 * @param profile - Profile with is_pro, is_test_user, test_pro_mode, subscription_tier, billing_source, entitlement_expires_at
 * @returns 'free' | 'social' | 'pro'
 */
export function effectiveTier(profile: EntitlementProfile | null | undefined): EntitlementTier {
  if (!profile) {
    return 'free';
  }

  const { is_pro, is_test_user, test_pro_mode, subscription_tier, billing_source, entitlement_expires_at } = profile;

  // Test user with explicit override takes priority
  if (is_test_user && test_pro_mode && test_pro_mode !== 'auto') {
    return test_pro_mode as EntitlementTier;
  }

  // Manual entitlement expiry check — expired manual grants revert to free
  if (billing_source === 'manual' && subscription_tier && subscription_tier !== 'free' && entitlement_expires_at) {
    const expiresAt = new Date(entitlement_expires_at);
    if (expiresAt.getTime() < Date.now()) {
      // Manual grant has expired — treat as free
      // (Real provider webhooks will reassert paid state if applicable)
      return 'free';
    }
  }

  // subscription_tier is canonical when present and not 'free'
  if (subscription_tier && subscription_tier !== 'free') {
    return subscription_tier as EntitlementTier;
  }

  // Legacy fallback
  return is_pro ? 'pro' : 'free';
}

/**
 * Get whether user is effectively Pro.
 *
 * @param profile - Profile with is_pro, is_test_user, test_pro_mode
 * @returns boolean
 */
export function effectiveIsPro(profile: EntitlementProfile | null | undefined): boolean {
  return effectiveTier(profile) === 'pro';
}
